import { Box, Button, Divider, Tooltip } from '@mui/material';
import { useParams, useSearch } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Query } from 'src/components/atoms/query';
import Page from 'src/components/organisms/page/page';
import { Pagination } from 'src/components/organisms/pagination';
import { fetchBookings } from 'src/features/bookings/api/lib/bookings';
import { bookingsQueryKeys } from 'src/features/bookings/api/query-keys/bookings';
import { useEmployeesGroupsByTokens } from 'src/features/employees-groups/api/hooks/use-employees-groups-by-tokens';
import { fetchSpaceGroups } from 'src/features/space-groups/api/lib/space-groups';
import { spaceGroupsKeys } from 'src/features/space-groups/api/query-keys/space-groups';
import { useMeilisearch } from 'src/hooks/meilisearch/use-meilisearch';
import { useMeilisearchWithUrlParams } from 'src/hooks/meilisearch/use-meilisearch-with-url-params';
import { useDialog } from 'src/hooks/use-dialog';
import { useSortController } from 'src/hooks/use-sort-controller';
import { Plus } from 'src/icons/plus';
import { Can, withPermissions } from 'src/permissions/can';
import { useUserStore } from 'src/store/user-store';
import { SortOrder } from 'src/types/enum/sort-order';
import { EmployeesGroupDetailsSpaceGroupsActionsTableMenu } from '../../molecules/employees-group-details/space-groups/space-groups-actions-table-menu';
import { AssignSpaceGroupsToEmployeeGroupDialog } from '../../organisms/dialogs/employees-group-details/space-groups/assign-space-groups-to-employee-group-dialog';
import { EmployeesHaveActiveOrFutureBookingsDialog } from '../../organisms/dialogs/employees-group-details/space-groups/employees-have-active-or-future-bookings-dialog';
import { UnassignSpaceGroupFromEmployeeGroup } from '../../organisms/dialogs/employees-group-details/space-groups/unassign-space-group-from-employee-group';
import { EmployeesGroupDetailsSpaceGroupsTable } from '../../organisms/employees-group-details/space-groups/space-groups-table';
import { SpaceGroupsActionsSlotPayload } from '../../organisms/employees-group-details/space-groups/space-groups-table-row';

const EmployeesGroupDetailsSpaceGroups = () => {
  const { t } = useTranslation(['employeesGroups', 'common']);

  const { directoryToken } = useUserStore();

  const { query, perPage, page } = useSearch({
    from: '/_layout/tenant/employees/groups/$groupToken/_layout/space-groups',
    select(state) {
      return {
        page: state.page,
        perPage: state.limit,
        query: state.q,
        filter: state.filter,
      };
    },
    structuralSharing: true,
  });

  const { groupToken } = useParams({
    from: '/_layout/tenant/employees/groups/$groupToken/_layout/space-groups',
  });

  const { results: employeeGroup } = useEmployeesGroupsByTokens([groupToken]);

  const employeeSpaceGroupsTokens = useMemo(
    () =>
      employeeGroup?.hits?.[0]?.spaceGroupsTokens || [
        'placeholder-to-not-load-all-groups',
      ],
    [employeeGroup?.hits],
  );

  const [sortController, setSortController] = useSortController({
    sortBy: 'name',
    sortOrder: SortOrder.DESC,
  });

  const defaultSearchStateConfig = useMemo(() => {
    return {
      sort: [`${sortController.sortBy}:${sortController.sortOrder}`],
      filter: {
        token: {
          value: employeeSpaceGroupsTokens,
        },
      },
    };
  }, [
    employeeSpaceGroupsTokens,
    sortController.sortBy,
    sortController.sortOrder,
  ]);

  const {
    results,
    handleChangeQuery,
    handleChangePage,
    isLoading,
    isFetching,
    handleChangeLimit,
    error,
    handleChangeSort,
  } = useMeilisearchWithUrlParams(
    fetchSpaceGroups,
    spaceGroupsKeys.list(directoryToken ?? '', page, perPage),
    defaultSearchStateConfig,
    employeeSpaceGroupsTokens && employeeSpaceGroupsTokens.length > 0,
  );

  const totalPages = Math.ceil((results?.estimatedTotalHits ?? 0) / perPage);

  const [selectedSpaceGroup, setSelectedSpaceGroup] =
    useState<SpaceGroupsActionsSlotPayload | null>(null);

  // Assign space groups to employee group
  const [
    isOpenAssignSpaceGroupsToEmployeeGroupDialog,
    handleOpenAssignSpaceGroupsToEmployeeGroupDialog,
    handleCloseAssignSpaceGroupsToEmployeeGroupDialog,
  ] = useDialog();

  // Unassign space group from employee group
  const [
    isOpenUnassignSpaceGroupFromEmployeeGroupDialog,
    handleOpenUnassignSpaceGroupFromEmployeeGroupDialog,
    handleCloseUnassignSpaceGroupFromEmployeeGroupDialog,
  ] = useDialog();

  // Employees have active or future bookings
  const [
    isOpenEmployeesHaveActiveOrFutureBookingsDialog,
    handleOpenEmployeesHaveActiveOrFutureBookingsDialog,
    handleCloseEmployeesHaveActiveOrFutureBookingsDialog,
  ] = useDialog();

  const { results: bookings } = useMeilisearch({
    fetch: fetchBookings,
    queryKey: bookingsQueryKeys.list(directoryToken ?? '', 1, 1000),
    page: 1,
    perPage: 1000,
    searchState: {
      filter: [
        results?.hits.map(({ token }) => `spaceGroupToken = ${token}`) ?? [],
        ['status = "BOOKED"'],
      ],
      sort: [],
      q: '',
    },
  });

  const actionsSlot = useCallback(
    ({ token, name, employeeGroupsTokens }: SpaceGroupsActionsSlotPayload) => (
      <EmployeesGroupDetailsSpaceGroupsActionsTableMenu
        onRemove={() => {
          setSelectedSpaceGroup({ token, name, employeeGroupsTokens });

          const selectedSpaceGroupBookings = bookings?.hits.filter(
            ({ spaceGroupToken }) => spaceGroupToken === token,
          );

          if (selectedSpaceGroupBookings?.length) {
            handleOpenEmployeesHaveActiveOrFutureBookingsDialog();
            return;
          }

          handleOpenUnassignSpaceGroupFromEmployeeGroupDialog();
        }}
      />
    ),
    [bookings?.hits],
  );

  const bookingsToManage = useMemo(() => {
    return (
      bookings?.hits.filter(
        ({ spaceGroupToken }) => spaceGroupToken === selectedSpaceGroup?.token,
      ) ?? []
    );
  }, [selectedSpaceGroup]);

  return (
    <>
      <Page.Header
        title={t('employeesGroups:spaceGroups.title')}
        headerVariant="h5">
        <Can I="create" a="EmployeesGroupSpaceGroups" passThrough>
          {allowed => (
            <Tooltip
              title={t('common:notSufficientPermissions')}
              disableHoverListener={allowed}
              disableFocusListener={allowed}>
              <span>
                <Button
                  sx={{ ml: 2 }}
                  onClick={handleOpenAssignSpaceGroupsToEmployeeGroupDialog}
                  color="primary"
                  disabled={!allowed}
                  startIcon={<Plus fontSize="small" />}
                  variant="contained"
                  id="assign_space_group_button"
                  size="large">
                  {t('employeesGroups:spaceGroups.addFromExisting')}
                </Button>
              </span>
            </Tooltip>
          )}
        </Can>
      </Page.Header>

      <Page.ContentContainer>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            px: 3,
            py: 2.5,
          }}>
          <Query
            searchAsType
            disabled={false}
            onChange={handleChangeQuery}
            sx={{
              minWidth: 200,
            }}
            value={query}
          />
        </Box>

        <EmployeesGroupDetailsSpaceGroupsTable
          data={results?.hits ?? []}
          isLoading={isLoading}
          error={error?.response?.data?.message}
          sortController={sortController}
          setSortController={setSortController}
          handleChangeSort={handleChangeSort}
          actionsSlot={actionsSlot}
        />

        <Divider sx={{ mt: 'auto' }} />

        <Pagination
          disabled={isLoading}
          isFetching={isFetching}
          changePage={handleChangePage}
          rowsCount={results?.estimatedTotalHits || 0}
          page={page}
          perPage={perPage}
          totalPages={totalPages}
          handlePerPageChange={handleChangeLimit}
        />
      </Page.ContentContainer>

      <AssignSpaceGroupsToEmployeeGroupDialog
        open={isOpenAssignSpaceGroupsToEmployeeGroupDialog}
        onCancel={handleCloseAssignSpaceGroupsToEmployeeGroupDialog}
        employeeSpaceGroupsTokens={employeeSpaceGroupsTokens}
      />

      <UnassignSpaceGroupFromEmployeeGroup
        open={isOpenUnassignSpaceGroupFromEmployeeGroupDialog}
        onCancel={handleCloseUnassignSpaceGroupFromEmployeeGroupDialog}
        spaceGroupToken={selectedSpaceGroup?.token ?? ''}
        spaceGroupName={selectedSpaceGroup?.name ?? ''}
        employeeGroupName={employeeGroup?.hits?.[0]?.name ?? ''}
      />

      <EmployeesHaveActiveOrFutureBookingsDialog
        open={isOpenEmployeesHaveActiveOrFutureBookingsDialog}
        onCancel={handleCloseEmployeesHaveActiveOrFutureBookingsDialog}
        spaceGroupToken={selectedSpaceGroup?.token ?? ''}
        bookingsToManage={bookingsToManage}
      />
    </>
  );
};

export default withPermissions(
  permissions => permissions.cannot('read', 'EmployeesGroupSpaceGroups'),
  EmployeesGroupDetailsSpaceGroups,
);
